/**
 * Ear-training examples are generated, not stored.
 *
 * If the same C-to-E were played every time, the learner would memorise the
 * example rather than the interval. Each attempt picks a fresh root, so the only
 * thing that stays constant is the relationship being asked about.
 */

import { QUALITY_BY_ID, spellChord } from '../theory/chords';
import { MAJOR_SCALE_SEMITONES, transposeBy, type Pitch } from '../theory/pitch';
import type { EarTask } from './types';
import type { PlayMode } from '../audio/player';

export interface EarExample {
  pitches: Pitch[];
  answer: string;
  mode: PlayMode;
}

const ROOTS: Pitch[] = [
  { letter: 'C', alter: 0, octave: 4 },
  { letter: 'D', alter: 0, octave: 4 },
  { letter: 'E', alter: 0, octave: 4 },
  { letter: 'F', alter: 0, octave: 4 },
  { letter: 'G', alter: 0, octave: 3 },
  { letter: 'A', alter: 0, octave: 3 },
  { letter: 'B', alter: -1, octave: 3 },
];

/**
 * Each interval as the two numbers that define it: how many letters it spans and
 * how many semitones it covers. Semitones alone are not enough — a minor third
 * and an augmented second are both three semitones, and the whole point of the
 * exercise is that they are different intervals.
 *
 * "Tritone" is read as an augmented fourth: three whole tones, which is what the
 * word says. Its enharmonic twin, the diminished fifth, spans five letters. The
 * choice is recorded in docs/THEORY-SOURCES.md because the name alone does not
 * settle it.
 */
export const EAR_INTERVALS: Record<string, { steps: number; semitones: number }> = {
  'Minor 2nd': { steps: 1, semitones: 1 },
  'Major 2nd': { steps: 1, semitones: 2 },
  'Minor 3rd': { steps: 2, semitones: 3 },
  'Major 3rd': { steps: 2, semitones: 4 },
  'Perfect 4th': { steps: 3, semitones: 5 },
  Tritone: { steps: 3, semitones: 6 },
  'Perfect 5th': { steps: 4, semitones: 7 },
  'Minor 6th': { steps: 5, semitones: 8 },
  'Major 6th': { steps: 5, semitones: 9 },
  'Minor 7th': { steps: 6, semitones: 10 },
  'Major 7th': { steps: 6, semitones: 11 },
  Octave: { steps: 7, semitones: 12 },
};

export const QUALITY_BY_LABEL: Record<string, string> = {
  'Major triad': 'maj',
  'Minor triad': 'min',
  'Diminished triad': 'dim',
  'Augmented triad': 'aug',
  'Major seventh': 'maj7',
  'Dominant seventh': 'dom7',
  'Minor seventh': 'min7',
  'Half-diminished seventh': 'm7b5',
};

/**
 * Scale degrees of the major scale. Degree n is n-1 letters up, which is why the
 * 3rd of E is G♯ and never A♭ — a third has to land on the third letter.
 */
export const EAR_DEGREES: Record<string, { steps: number; semitones: number }> = Object.fromEntries(
  ['1', '2', '3', '4', '5', '6', '7'].map((degree) => {
    const steps = Number(degree) - 1;
    return [degree, { steps, semitones: MAJOR_SCALE_SEMITONES[steps] }];
  }),
);

/**
 * Transpose, keeping the spelling.
 *
 * This used to name the result from a twelve-entry pitch-class table, which is
 * lossy in exactly the way the subject is about: a minor third above B♭ came
 * back as C♯ — an augmented second — so the ear task's own answer contradicted
 * the notes it played. `transposeBy` picks the letter from the interval first.
 */
function transpose(pitch: Pitch, interval: { steps: number; semitones: number }): Pitch {
  return transposeBy(pitch, interval.steps, interval.semitones);
}

const pick = <T>(list: T[], random: () => number): T => list[Math.floor(random() * list.length)];

export function buildEarExample(task: EarTask, random: () => number = Math.random): EarExample {
  const root = pick(ROOTS, random);
  const answer = pick(task.choices, random);

  switch (task.kind) {
    case 'interval': {
      const interval = EAR_INTERVALS[answer] ?? EAR_INTERVALS['Major 3rd'];
      return { pitches: [root, transpose(root, interval)], answer, mode: 'both' };
    }

    case 'quality': {
      const quality = QUALITY_BY_ID[QUALITY_BY_LABEL[answer] ?? 'maj'];
      return { pitches: spellChord(root, quality), answer, mode: 'both' };
    }

    case 'scaleDegree': {
      const degree = EAR_DEGREES[answer] ?? EAR_DEGREES['1'];
      // Sound the tonic first, so the degree has something to be measured against.
      return { pitches: [root, transpose(root, degree)], answer, mode: 'arpeggio' };
    }

    case 'cadence': {
      // "Finished" is a dominant seventh resolving home; "unfinished" stops on the
      // dominant and leaves the tritone hanging.
      const tonic = spellChord(root, QUALITY_BY_ID.maj);
      const dominant = spellChord(transpose(root, EAR_INTERVALS['Perfect 5th']), QUALITY_BY_ID.dom7);
      const pitches = answer === 'Finished' ? [...dominant, ...tonic] : [...tonic, ...dominant];
      return { pitches, answer, mode: 'arpeggio' };
    }

    default:
      return { pitches: [root], answer, mode: 'block' };
  }
}
